import PropTypes from 'prop-types';
import React from 'react';
import {
  Button, Checkbox, LinkExternal01, Minus, Plus, Select, TextInput, Toggle,
} from '@opendate-app/setlist';

import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';

const DISCOUNT_TYPE_ITEMS = [
  { value: 'percentage', label: 'Percentage' },
  { value: 'amount', label: 'Fixed Amount' },
];

const cardStyle = {
  border: '1px solid var(--sl-border-secondary)',
  borderRadius: '8px',
  background: 'var(--sl-bg-primary)',
};
const mutedTextStyle = { color: 'var(--sl-text-tertiary)' };
const fieldLabelStyle = { color: 'var(--sl-text-secondary)', fontSize: '14px', fontWeight: 500 };

// "Gold members get 10% off VIP, up to 2 tickets each. After that they pay standard price."
export const membershipBenefitSummary = ({ name, discountType, percentage, amount, appliesToTickets, ticketNames, holdNames = [], holdsAllReleased = false, appliesToAddOns, addOnNames, limit, household }) => {
  const isPercentage = discountType === 'percentage';
  if (isPercentage && (parseInt(percentage, 10) || 0) === 0) return null;
  if (!isPercentage && (parseFloat(String(amount).replace(/[^0-9.]/g, '')) || 0) === 0) return null;
  const discount = isPercentage
    ? `${parseInt(percentage, 10) || 0}%`
    : `$${String(amount).replace(/[^0-9.]/g, '') || '0.00'}`;

  const targets = [];
  if (appliesToTickets) {
    const ticketTargets = [...ticketNames];
    if (holdNames.length > 0) ticketTargets.push(`seats in ${holdNames.join(', ')}`);
    if (ticketTargets.length > 0) targets.push(ticketTargets.join(', '));
    else if (!holdsAllReleased) targets.push('all tickets');
  }
  const noTicketDiscount = appliesToTickets && holdsAllReleased && ticketNames.length === 0 && holdNames.length === 0;
  if (appliesToAddOns) targets.push(addOnNames.length > 0 ? addOnNames.join(', ') : 'all add-ons');
  if (targets.length === 0) return noTicketDiscount ? `${name} members get no discount until you pick a hold or ticket type.` : null;

  const perMember = parseInt(limit, 10) || 0;
  const limitClause = perMember > 0
    ? `, up to ${perMember} ${perMember === 1 ? 'ticket' : 'tickets'} ${household ? 'per household' : 'each'}. After that they pay standard price.`
    : '.';

  return `${name} members get ${discount} off ${targets.join(' and ')}${limitClause}${noTicketDiscount ? ' Tickets get no discount until you pick a hold or ticket type.' : ''}`;
};

// The confirm's membership card, without its server calls, so the series
// wizard offers memberships in the same card.
const MembershipProductCardView = ({
  idKey, name, editUrl, priceLabel, description, imageUrl,
  sellChecked, onSellToggle, showAnnual, annualPrice, onAnnualPriceChange, showMonthly, monthlyPrice, onMonthlyPriceChange, priceHint,
  waivesOrganizerFees, organizerFeesWaived, onOrganizerFeesWaivedChange,
  benefitsEnabled, onBenefitsToggle, discountType, onDiscountTypeChange, percentage, onPercentageChange, amount, onAmountChange,
  limit, onLimitChange, onLimitStep, household,
  appliesToTickets, onAppliesToTicketsChange, ticketOptions, selectedTicketIds, onTicketsChange,
  holdOptions, selectedHoldIds, onHoldsChange,
  appliesToAddOns, onAppliesToAddOnsChange, addOnOptions, selectedAddOnIds, onAddOnsChange,
  summary,
}) => {
  return (
    <div className="mb-3 p-3" style={cardStyle}>
      <div className="d-flex align-items-start">
        <div className="flex-grow-1">
          <p className="mb-1">
            <a href={editUrl}
               target="_blank"
               rel="noopener noreferrer"
               className="font-weight-bold"
               style={{ color: 'inherit', textDecoration: 'none' }}>
              {name} <LinkExternal01 width={12} height={12} aria-hidden="true" />
            </a>
            {priceLabel ? (
              <span className="ml-1" style={mutedTextStyle}>
                {priceLabel}
              </span>
            ) : null}
          </p>
          {description ? (
            <p className="mb-0 small" style={{
              ...mutedTextStyle,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}>
              {description}
            </p>
          ) : null}
        </div>
        {imageUrl && (
          <img
            src={imageUrl}
            alt={name}
            className="ml-3 rounded"
            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
          />
        )}
      </div>

      <div className="mt-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span className="font-weight-medium">Sell during checkout</span>
          <Toggle
            id={`checkout-${idKey}`}
            aria-label="Sell during checkout"
            checked={sellChecked}
            onChange={onSellToggle}
          />
        </div>
        {sellChecked && (showAnnual || showMonthly) && (
          <div className="d-flex flex-column mb-2" style={{ gap: '8px' }}>
            {showAnnual && (
              <TextInput
                as={CurrencyInput}
                label="Annual Event Price"
                leadingText="$"
                maskOptions={{ prefix: '', integerLimit: null }}
                value={annualPrice}
                onChange={onAnnualPriceChange}
                placeholder="0.00"
              />
            )}
            {showMonthly && (
              <TextInput
                as={CurrencyInput}
                label="Monthly Event Price"
                leadingText="$"
                maskOptions={{ prefix: '', integerLimit: null }}
                value={monthlyPrice}
                onChange={onMonthlyPriceChange}
                placeholder="0.00"
              />
            )}
            <p className="mb-0" style={{ ...mutedTextStyle, fontSize: '12px' }}>
              {priceHint}
            </p>
          </div>
        )}
      </div>

      <div className="mt-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span className="font-weight-medium">Membership benefits</span>
          <Toggle
            id={`promo-${idKey}`}
            aria-label="Membership benefits"
            checked={benefitsEnabled}
            onChange={onBenefitsToggle}
          />
        </div>
        {benefitsEnabled && (
          <div className="p-3" style={cardStyle}>
            <div className="mb-3">
              <div role="group" aria-label="Discount type" className="d-inline-flex" style={{ gap: '8px' }}>
                {DISCOUNT_TYPE_ITEMS.map((item) => {
                  const active = item.value === discountType;

                  return (
                    <Button
                      key={item.value}
                      size="sm"
                      variant={active ? 'secondary-color' : 'secondary'}
                      aria-pressed={active}
                      onClick={() => {
                        if (active) return;

                        onDiscountTypeChange(item.value);
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-12 col-md-4 col-lg-3">
                {discountType === 'percentage' ? (
                  <TextInput
                    label="Percentage off"
                    inputMode="numeric"
                    trailingText="%"
                    value={percentage}
                    onChange={onPercentageChange}
                  />
                ) : (
                  <TextInput
                    as={CurrencyInput}
                    label="Amount off"
                    leadingText="$"
                    maskOptions={{ prefix: '', integerLimit: null }}
                    value={amount}
                    onChange={onAmountChange}
                    placeholder="0.00"
                  />
                )}
              </div>
              <div className="col-12 col-md-5 col-lg-4 mt-3 mt-md-0">
                <label htmlFor={`limit-per-member-${idKey}`} className="d-block mb-1" style={fieldLabelStyle}>
                  Discounted tickets per {household ? 'household' : 'member'}
                </label>
                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                  <Button
                    variant="secondary"
                    icon={Minus}
                    aria-label="Decrease limit"
                    onClick={() => onLimitStep(-1)}
                  />
                  <div style={{ flex: 1 }}>
                    <TextInput
                      id={`limit-per-member-${idKey}`}
                      inputMode="numeric"
                      style={{ textAlign: 'center' }}
                      value={limit}
                      onChange={onLimitChange}
                    />
                  </div>
                  <Button
                    variant="secondary"
                    icon={Plus}
                    aria-label="Increase limit"
                    onClick={() => onLimitStep(1)}
                  />
                </div>
              </div>
            </div>
            <div>
              <p className="mb-2" style={fieldLabelStyle}>Apply discount to</p>
              <div className="mb-2">
                <Checkbox
                  id={`applies-tickets-${idKey}`}
                  label="Tickets"
                  checked={appliesToTickets}
                  onChange={(e) => onAppliesToTicketsChange(e.target.checked)}
                />
                {appliesToTickets && (
                  <div className="ml-4 mt-1">
                    <Select
                      aria-label="Ticket types"
                      multiple
                      searchable
                      options={ticketOptions}
                      value={selectedTicketIds}
                      onChange={onTicketsChange}
                      placeholder="Search ticket types..."
                      hint="Leave empty to apply to all ticket types. Select a hidden ticket type to reveal it to members."
                    />
                    {holdOptions.length > 0 && (
                      <div className="mt-2">
                        <Select
                          label="Unlocks holds"
                          aria-label="Unlocks holds"
                          multiple
                          searchable
                          options={holdOptions}
                          value={selectedHoldIds}
                          onChange={onHoldsChange}
                          placeholder="Search holds..."
                          hint="Members can buy seats in these holds. The discount covers seats in these holds plus any ticket types picked above."
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="mb-2">
                <Checkbox
                  id={`applies-addons-${idKey}`}
                  label="Add-ons"
                  checked={appliesToAddOns}
                  onChange={(e) => onAppliesToAddOnsChange(e.target.checked)}
                />
                {appliesToAddOns && (
                  <div className="ml-4 mt-1">
                    <Select
                      aria-label="Add-ons"
                      multiple
                      searchable
                      options={addOnOptions}
                      value={selectedAddOnIds}
                      onChange={onAddOnsChange}
                      placeholder="Search add-ons..."
                      hint="Leave empty to apply to all add-ons."
                    />
                  </div>
                )}
              </div>
            </div>
            {waivesOrganizerFees && (
              <div className="mt-2">
                <Checkbox
                  id={`no-organizer-fees-${idKey}`}
                  label="No organizer fees"
                  checked={organizerFeesWaived}
                  onChange={(e) => onOrganizerFeesWaivedChange(e.target.checked)}
                />
                <p className="mb-0 ml-4" style={{ ...mutedTextStyle, fontSize: '12px' }}>
                  Members skip organizer fees on tickets for this show.
                </p>
              </div>
            )}
            {summary ? (
              <div
                className="mt-3 p-3"
                style={{
                  backgroundColor: 'var(--sl-bg-brand-primary)',
                  border: '1px solid var(--sl-border-brand)',
                  borderRadius: 'var(--sl-radius-md)',
                  color: 'var(--sl-text-primary)',
                  fontSize: '13px',
                }}
              >
                {summary}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

MembershipProductCardView.propTypes = {
  idKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  name: PropTypes.string.isRequired,
  editUrl: PropTypes.string,
  priceLabel: PropTypes.string,
  description: PropTypes.string,
  imageUrl: PropTypes.string,
  sellChecked: PropTypes.bool,
  onSellToggle: PropTypes.func,
  showAnnual: PropTypes.bool,
  annualPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onAnnualPriceChange: PropTypes.func,
  showMonthly: PropTypes.bool,
  monthlyPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onMonthlyPriceChange: PropTypes.func,
  priceHint: PropTypes.string,
  waivesOrganizerFees: PropTypes.bool,
  organizerFeesWaived: PropTypes.bool,
  onOrganizerFeesWaivedChange: PropTypes.func,
  benefitsEnabled: PropTypes.bool,
  onBenefitsToggle: PropTypes.func,
  discountType: PropTypes.string,
  onDiscountTypeChange: PropTypes.func,
  percentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onPercentageChange: PropTypes.func,
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onAmountChange: PropTypes.func,
  limit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onLimitChange: PropTypes.func,
  onLimitStep: PropTypes.func,
  household: PropTypes.bool,
  appliesToTickets: PropTypes.bool,
  onAppliesToTicketsChange: PropTypes.func,
  ticketOptions: PropTypes.array,
  selectedTicketIds: PropTypes.array,
  onTicketsChange: PropTypes.func,
  holdOptions: PropTypes.array,
  selectedHoldIds: PropTypes.array,
  onHoldsChange: PropTypes.func,
  appliesToAddOns: PropTypes.bool,
  onAppliesToAddOnsChange: PropTypes.func,
  addOnOptions: PropTypes.array,
  selectedAddOnIds: PropTypes.array,
  onAddOnsChange: PropTypes.func,
  summary: PropTypes.node,
};

MembershipProductCardView.defaultProps = {
  holdOptions: [],
  selectedHoldIds: [],
  onHoldsChange: null,
  summary: null,
};

export default MembershipProductCardView;
